import { Link, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAuth } from '../contexts/AuthContext';
import { Alert, Button, Input, Card, CardHeader, CardTitle, CardContent } from '../components/ui';

const loginSchema = z.object({
  username: z.string().email('Please enter a valid email address'),
  password: z.string().min(1, 'Password is required'),
});

type LoginFormData = z.infer<typeof loginSchema>;

export default function LoginPage() {
  const { login } = useAuth();
  const [searchParams] = useSearchParams();
  const registered = searchParams.get('registered') === 'true';
  const message = searchParams.get('message');
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setError,
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  });

  const onSubmit = async (data: LoginFormData) => {
    try {
      await login(data);
    } catch (error: unknown) {
      // Handle different error response formats
      let errorMessage = 'Login failed. Please try again.';

      if (error && typeof error === 'object') {
        const err = error as { response?: { data?: { detail?: string | { msg?: string }[] } } };

        if (err.response?.data?.detail) {
          const detail = err.response.data.detail;

          // Handle string detail (standard error message)
          if (typeof detail === 'string') {
            errorMessage = detail;
          }
          // Handle array detail (Pydantic validation errors)
          else if (Array.isArray(detail) && detail.length > 0) {
            errorMessage = detail[0].msg || 'Validation error occurred';
          }
        }
      }

      setError('root', {
        type: 'manual',
        message: errorMessage,
      });
    }
  };

  const handleForgotPassword = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    alert('Password reset functionality coming soon. Please contact support@permitbuddy.com for assistance.');
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-gray-50 to-blue-50 px-4 py-12">
      <Card variant="elevated" padding="lg" className="w-full max-w-md">
        <CardHeader className="text-center mb-6">
          <CardTitle as="h1" className="text-3xl">Welcome Back</CardTitle>
          <p className="text-gray-600 mt-2">Sign in to your PermitBuddy account</p>
        </CardHeader>

        <CardContent>
          {registered && (
            <Alert variant="success" className="mb-6" showIcon>
              {message || 'Registration successful! Please check your email to verify your account before signing in.'}
            </Alert>
          )}
          {errors.root && (
            <Alert variant="error" className="mb-6" showIcon>
              {errors.root.message || 'An error occurred'}
            </Alert>
          )}

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <Input
              {...register('username')}
              type="email"
              id="username"
              label="Email Address"
              placeholder="you@example.com"
              error={errors.username?.message}
              disabled={isSubmitting}
              inputSize="md"
            />

            <Input
              {...register('password')}
              type="password"
              id="password"
              label="Password"
              placeholder="••••••••"
              error={errors.password?.message}
              disabled={isSubmitting}
              inputSize="md"
            />

            <div className="flex items-center justify-between text-sm">
              <label className="flex items-center gap-2 text-gray-600">
                <input
                  type="checkbox"
                  className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary"
                />
                Remember me
              </label>
              <a href="#forgot" onClick={handleForgotPassword} className="font-medium text-primary hover:text-primary-dark">
                Forgot password?
              </a>
            </div>

            <Button
              type="submit"
              variant="primary"
              size="lg"
              fullWidth
              isLoading={isSubmitting}
            >
              {isSubmitting ? 'Signing In...' : 'Sign In'}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600">
              Don't have an account?{' '}
              <Link to="/register" className="font-semibold text-primary hover:text-primary-dark">
                Sign up for free
              </Link>
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
